import { useAtom, useSetAtom } from 'jotai'
import { useDispatch, useSelector } from 'react-redux'
import { orderInvoicePanel } from 'src/atoms/modals/orderInvoicePanel.atom'
import { orderManagerModal } from 'src/atoms/modals/orderManagerModal.atom'
import { orderTimelineModal } from 'src/atoms/modals/orderTimelineModal.atom'
import { calendarSelectorPopover } from 'src/atoms/modals/calendarSelectorPopover'
import {
  OverlayAction,
  OverlayResource
} from 'src/componentsV2/CoreUX/actions/overlayAction/overlayAction.types'
import { toggleSchedulePanel } from 'src/redux-api-bridge/schedule/effects'
import { openTrackDeliveryPanel } from 'src/redux/slices/trackDeliveryPanel/slice'
import { useCart } from './useCart'
import { useSkipOrderDeflection } from './useSkipOrderDeflection'
import { useUnskip } from './useUnskip'
import { orderHubCart } from 'src/atoms/modals/orderHubCart.atom'
import {
  dayByDeliveryDateSelector,
  timeslotSelector
} from 'src/components/DeliveryCard/selectors'
import { useFlag } from '@unleash/proxy-client-react'
import { FF_INTERACTIVE_CENTER } from 'src/constants/feature-flags'
import { useHistory } from 'react-router-dom'
import { userOnboardingModalAtom } from 'src/atoms/modals/userOnboardingModal.atom'

interface UseOverlayManagerReturn {
  showOverlay: (action: OverlayAction) => void
}

export const useOverlayManager = (): UseOverlayManagerReturn => {
  const dispatch = useDispatch()
  const history = useHistory()
  const setIsOpenCalendarSelector = useSetAtom(calendarSelectorPopover)
  const setUserOnboardingModal = useSetAtom(userOnboardingModalAtom)
  const setOrderHubCart = useSetAtom(orderHubCart)
  const [{ date: orderManagerDate }, setIsOpenOrderManager] = useAtom(
    orderManagerModal
  )
  const setIsOpenOrderTimeline = useSetAtom(orderTimelineModal)
  const setIsOpenOrderInvoice = useSetAtom(orderInvoicePanel)
  const { setIsCartOpen } = useCart()
  const { handleOnSkip } = useSkipOrderDeflection({ date: orderManagerDate })
  const { unskipIfRequired } = useUnskip(orderManagerDate, {
    page_name: 'Orders - Manage your order'
  })
  const timeslot = useSelector(state =>
    timeslotSelector(state, orderManagerDate)
  )
  const day = useSelector(state =>
    dayByDeliveryDateSelector(state, orderManagerDate)
  )
  const interactiveCenterEnabled = useFlag(FF_INTERACTIVE_CENTER)

  const showOverlay = (action: OverlayAction): void => {
    switch (action.data.resource) {
      case OverlayResource.HomeCalendarSelector:
        setIsOpenCalendarSelector({
          isOpen: true,
          date: action.data.parameters?.date
        })
        break
      case OverlayResource.AddExtraDeliveryDay:
        dispatch(
          toggleSchedulePanel(
            'extraDelivery',
            true,
            action.data.parameters?.date
          )
        )
        break
      case OverlayResource.OrderManagement:
        setIsOpenOrderManager({
          isOpen: true,
          date: action.data.parameters?.date
        })
        break
      case OverlayResource.OrderInvoice:
        setIsOpenOrderInvoice({
          isOpen: true,
          date: action.data.parameters?.date
        })
        break
      case OverlayResource.OrderTimeline:
        setIsOpenOrderTimeline({
          isOpen: true,
          date: action.data.parameters?.date
        })
        break
      case OverlayResource.RescheduleOrder:
        setIsOpenOrderManager({
          isOpen: false,
          date: action.data.parameters?.date
        })
        dispatch(
          toggleSchedulePanel(
            'reschedule',
            true,
            day,
            timeslot,
            day.cutoff,
            null
          )
        )
        break
      case OverlayResource.OrderTracking:
        dispatch(openTrackDeliveryPanel(action.data.parameters?.type))
        break
      case OverlayResource.CartDetail:
        setOrderHubCart({ date: action.data.parameters?.date })
        setIsCartOpen(true)
        break
      case OverlayResource.OrderSkip:
        setIsOpenOrderManager({
          isOpen: false,
          date: action.data.parameters?.date
        })
        handleOnSkip()
        break
      case OverlayResource.OrderUnskip:
        setIsOpenOrderManager({
          isOpen: false,
          date: action.data.parameters?.date
        })
        unskipIfRequired()
        break
      case OverlayResource.NextSteps:
        if (interactiveCenterEnabled) {
          history.push('/onboarding-hub?section=next-steps')
        } else {
          setUserOnboardingModal({
            isOpen: true
          })
        }
        break
    }
  }

  return { showOverlay }
}
